/**
 * Il banco: cosa c'e' in vetrina adesso.
 *
 * Questa pagina vive su un tablet appoggiato al banco, e tutto qui e' pensato
 * per quello: tocchi grandi, poche parole, nessun menu da cercare. Una
 * banconista con le mani occupate deve poter spegnere un gusto finito in un
 * gesto solo, mentre serve un cliente.
 *
 * Si aggiorna da sola perche' sullo stesso banco possono esserci due tablet, e
 * il monitor in vetrina mostra la stessa lista: se una spegne il pistacchio,
 * l'altra lo deve vedere spento senza ricaricare niente.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { RefreshCw, Check, X, IceCreamCone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ApiService, { GustoBanco, PuntoVenditaBanco } from '../services/api';
import { PageContainerWide, PageHeader, Alert, LoadingState } from '../components/ui';

/** Ogni quanto la pagina si riallinea da sola, in millisecondi. */
const INTERVALLO_AGGIORNAMENTO = 30000;

const Intestazione = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-md);
  margin-bottom: var(--space-lg);
`;

const Conteggio = styled.div`
  font-size: var(--font-size-lg);

  strong {
    font-size: 2rem;
    font-variant-numeric: tabular-nums;
  }

  span {
    display: block;
    color: var(--text-light);
    font-size: var(--font-size-sm);
  }
`;

const Sezione = styled.section`
  margin-bottom: var(--space-2xl);

  h2 {
    font-size: var(--font-size-md);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-secondary);
    margin-bottom: var(--space-md);
  }
`;

const Griglia = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: var(--space-md);
`;

/**
 * La scheda del gusto e' un pulsante grande: su un tablet il bersaglio deve
 * essere ampio, e non ci deve essere niente altro da toccare per sbaglio.
 */
const Scheda = styled.button<{ $inVetrina: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-lg) var(--space-md);
  border-radius: var(--radius-lg);
  border: 2px solid ${(p) => (p.$inVetrina ? 'var(--success-500, #2f9e44)' : 'var(--gray-200)')};
  background: ${(p) => (p.$inVetrina ? 'var(--bg-primary, #fff)' : 'var(--gray-50)')};
  cursor: pointer;
  text-align: center;
  min-height: 200px;
  transition: transform 0.08s ease, box-shadow 0.15s ease;
  opacity: ${(p) => (p.$inVetrina ? 1 : 0.55)};

  &:active { transform: scale(0.97); }
  &:disabled { cursor: wait; }

  .foto {
    width: 92px;
    height: 92px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    filter: ${(p) => (p.$inVetrina ? 'none' : 'grayscale(1)')};
  }

  .senza-foto {
    width: 92px;
    height: 92px;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .nome {
    font-weight: var(--font-weight-semibold);
    line-height: 1.25;
  }

  .stato {
    font-size: var(--font-size-xs);
    color: var(--text-light);
  }
`;

const Segno = styled.span<{ $inVetrina: boolean }>`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: ${(p) => (p.$inVetrina ? 'var(--success-500, #2f9e44)' : 'var(--gray-400, #adb5bd)')};
`;

const Vuoto = styled.div`
  text-align: center;
  padding: var(--space-2xl) var(--space-md);
  color: var(--text-light);
`;

const Nota = styled.p`
  color: var(--text-light);
  font-size: var(--font-size-sm);
  margin-top: var(--space-sm);
`;

const Selettore = styled.select`
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--radius-md);
  border: 1px solid var(--gray-300);
  font-size: var(--font-size-md);
`;

const oraItaliana = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '';

/**
 * Da quando la vetrina non si azzera piu' a mezzanotte, il conteggio da cui
 * parte puo' essere di ieri o della settimana scorsa: se non e' di oggi va
 * detto, altrimenti chi guarda non ha modo di sapere quanto e' vecchio.
 */
const etichettaGiorno = (data: string | null): string => {
  if (!data) return '';
  const giorno = data.slice(0, 10);
  const oggi = new Date().toLocaleDateString('sv-SE');
  if (giorno === oggi) return '';
  return ` del ${new Date(giorno).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}`;
};


const BancoPage: React.FC = () => {
  const { user } = useAuth();
  const [gusti, setGusti] = useState<GustoBanco[]>([]);
  const [punti, setPunti] = useState<PuntoVenditaBanco[]>([]);
  const [puntoVenditaId, setPuntoVenditaId] = useState<number | ''>(user?.punto_vendita_id || '');
  const [caricamento, setCaricamento] = useState(true);
  const [inCorso, setInCorso] = useState<number | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [ultimoAggiornamento, setUltimoAggiornamento] = useState<Date | null>(null);
  const [dataMagazzino, setDataMagazzino] = useState<string | null>(null);

  // Serve a non far sparire la lista mentre si ricarica: su un tablet al banco
  // una schermata che sfarfalla ogni trenta secondi e' insopportabile.
  const primaCarica = useRef(true);

  const carica = useCallback(async () => {
    if (puntoVenditaId === '') return;
    try {
      const esito = await ApiService.getGustiBanco(Number(puntoVenditaId));
      setGusti(esito.gusti);
      setDataMagazzino(esito.dataMagazzino || null);
      setUltimoAggiornamento(new Date());
      setErrore(null);
    } catch (e: any) {
      // Se la rete cade si tiene l'ultima lista buona invece di svuotare lo
      // schermo: meglio un dato di un minuto fa che nessun dato.
      setErrore(e?.response?.data?.error || 'Aggiornamento non riuscito, la lista potrebbe non essere aggiornata');
    } finally {
      primaCarica.current = false;
      setCaricamento(false);
    }
  }, [puntoVenditaId]);

  useEffect(() => {
    ApiService.getPuntiVenditaBanco()
      .then((elenco) => {
        setPunti(elenco);
        setPuntoVenditaId((precedente) => (precedente === '' && elenco.length ? elenco[0].id : precedente));
      })
      .catch(() => setPunti([]));
  }, []);

  useEffect(() => { carica(); }, [carica]);

  useEffect(() => {
    const timer = setInterval(carica, INTERVALLO_AGGIORNAMENTO);
    return () => clearInterval(timer);
  }, [carica]);

  const cambia = async (gusto: GustoBanco) => {
    if (puntoVenditaId === '') return;
    setInCorso(gusto.id);
    setErrore(null);
    try {
      const esito = await ApiService.impostaGustoBanco(
        gusto.id, !gusto.disponibile, Number(puntoVenditaId)
      );
      setGusti(esito.gusti);
      setDataMagazzino(esito.dataMagazzino || null);
      setUltimoAggiornamento(new Date());
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Non è stato possibile aggiornare il gusto');
    } finally {
      setInCorso(null);
    }
  };

  const inVetrina = gusti.filter((g) => g.disponibile);
  const fuori = gusti.filter((g) => !g.disponibile);

  const scheda = (gusto: GustoBanco) => (
    <Scheda key={gusto.id} $inVetrina={gusto.disponibile} disabled={inCorso === gusto.id}
      onClick={() => cambia(gusto)}
      title={gusto.disponibile ? 'Tocca se è finito' : 'Tocca per metterlo in vetrina'}>
      <Segno $inVetrina={gusto.disponibile}>
        {gusto.disponibile ? <Check size={16} /> : <X size={16} />}
      </Segno>
      {gusto.foto_url
        ? <img className="foto" src={gusto.foto_url} alt="" />
        : <div className="senza-foto" style={{ backgroundColor: gusto.colore }} />}
      <span className="nome">{gusto.nome}</span>
      <span className="stato">
        {gusto.scelta_manuale === null || gusto.scelta_manuale !== gusto.disponibile
          ? (gusto.in_magazzino
            ? `dal magazzino${etichettaGiorno(gusto.data_magazzino)}`
            : 'non risulta in magazzino')
          : `${gusto.disponibile ? 'acceso' : 'spento'} a mano${gusto.aggiornato_da ? ` da ${gusto.aggiornato_da}` : ''} ${oraItaliana(gusto.aggiornato_at)}`}
      </span>
    </Scheda>
  );

  if (caricamento && primaCarica.current) {
    return <PageContainerWide><LoadingState /></PageContainerWide>;
  }

  return (
    <PageContainerWide>
      <PageHeader
        title="Banco"
        subtitle="Tocca un gusto per accenderlo o spegnerlo. Il monitor e il sito cambiano subito."
        icon={<IceCreamCone size={24} />}
      />

      <Intestazione>
        <Conteggio>
          <strong>{inVetrina.length}</strong> gusti in vetrina
          <span>
            {/* La vetrina non riparte da zero ogni mattina: se l'inventario da
                cui parte non e' di oggi, va detto a chi sta al banco. */}
            {etichettaGiorno(dataMagazzino)
              && `dall'inventario${etichettaGiorno(dataMagazzino)} · `}
            {ultimoAggiornamento
              ? `aggiornato alle ${ultimoAggiornamento.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
              : ''}
          </span>
        </Conteggio>

        {punti.length > 1 && (
          <Selettore value={puntoVenditaId}
            onChange={(e) => setPuntoVenditaId(e.target.value ? Number(e.target.value) : '')}>
            {punti.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </Selettore>
        )}
      </Intestazione>

      {errore && <Alert $variant="warning">{errore}</Alert>}

      <Sezione>
        <h2>In vetrina</h2>
        {!inVetrina.length && (
          <Vuoto>
            Nessun gusto in vetrina. Se la produzione di oggi non è ancora stata registrata,
            accendi qui sotto i gusti che ci sono.
          </Vuoto>
        )}
        <Griglia>{inVetrina.map(scheda)}</Griglia>
      </Sezione>

      <Sezione>
        <h2>Non in vetrina</h2>
        {!fuori.length && <Vuoto>Tutti i gusti sono in vetrina.</Vuoto>}
        <Griglia>{fuori.map(scheda)}</Griglia>
        <Nota>
          <RefreshCw size={13} style={{ verticalAlign: '-2px' }} /> La lista si aggiorna da sola
          ogni mezzo minuto, e ogni mattina riparte da quello che risulta prodotto e in giacenza.
        </Nota>
      </Sezione>

    </PageContainerWide>
  );
};

export default BancoPage;
